// Shared UI Components — REDE Platform v2
import React, { useState, useEffect } from 'react';
import { getBrandPayload, subscribeBrand } from '../utils/brandState';
import { APP_VERSION } from '../utils/theme';

// ═══════════════════════════════════════════════════════════
// TOOLTIP
// ═══════════════════════════════════════════════════════════
export const Tooltip_ = ({text, children}) => {
  const [show, setShow] = useState(false);
  const [pos, setPos] = useState({top:0,left:0});
  const btnRef = React.useRef(null);
  const handleShow = (visible) => {
    if(visible && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      const tipWidth = 280;
      let left = rect.left + rect.width/2 - tipWidth/2;
      if(left < 8) left = 8;
      if(left + tipWidth > window.innerWidth - 8) left = window.innerWidth - tipWidth - 8;
      setPos({ top: rect.top - 8, left });
    }
    setShow(visible);
  };
  return (
    <span style={{display:'inline-flex',alignItems:'center'}}>
      {children}
      <span
        ref={btnRef}
        onMouseEnter={()=>handleShow(true)} onMouseLeave={()=>handleShow(false)}
        onClick={()=>handleShow(!show)}
        style={{
          display:'inline-flex',alignItems:'center',justifyContent:'center',
          width:18,height:18,borderRadius:'50%',marginLeft:6,cursor:'pointer',
          background:'var(--bg)',color:'var(--muted)',
          border:'1px solid var(--border)',
          fontSize:11,fontWeight:600,fontFamily:"'Inter',sans-serif",flexShrink:0,
        }}>?</span>
      {show && (
        <span style={{
          position:'fixed',top:pos.top,left:pos.left,transform:'translateY(-100%)',
          background:'var(--tooltip-bg)',color:'var(--text-soft)',
          border:'1px solid var(--tooltip-border)',borderRadius:10,padding:'10px 14px',
          fontSize:13,fontFamily:"'Inter',sans-serif",lineHeight:1.5,
          boxShadow:'var(--card-shadow)',whiteSpace:'normal',
          width:'max-content',maxWidth:280,zIndex:9999,
        }}>{text}</span>
      )}
    </span>
  );
};

// ═══════════════════════════════════════════════════════════
// CARD — shadow, not border (REDE standard)
// ═══════════════════════════════════════════════════════════
export const Card = ({children, style={}}) => (
  <div style={{
    background:'var(--card)',borderRadius:12,
    boxShadow:'var(--card-shadow)',border:'1px solid var(--border)',
    padding:'18px 20px',overflow:'hidden',...style
  }}>{children}</div>
);

// ═══════════════════════════════════════════════════════════
// SECTION LABEL — 11px Inter muted, NEVER uppercase (REDE v2)
// ═══════════════════════════════════════════════════════════
export const SectionLabel = ({children, tip}) => (
  <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:4,marginBottom:14,textAlign:'center'}}>
    <span style={{fontSize:11,fontWeight:700,color:'var(--muted)',fontFamily:"'JetBrains Mono', monospace",letterSpacing:'0.14em',textTransform:'uppercase'}}>{children}</span>
    {tip && <Tooltip_ text={tip}><span/></Tooltip_>}
  </div>
);

export const GoldDivider = ({width=120,mb=20}) => (
  <div style={{width,height:1,margin:`0 auto ${mb}px`,background:'linear-gradient(90deg,transparent,var(--gold),transparent)'}}/>
);

// ═══════════════════════════════════════════════════════════
// TAB BAR — REDE style: active = accent bg, white text, shadow
// ═══════════════════════════════════════════════════════════
export const TabBar = ({tabs, active, onChange}) => (
  <div style={{display:'flex',gap:4,overflowX:'auto',WebkitOverflowScrolling:'touch',padding:'4px 0',marginBottom:16}}>
    {tabs.map(t => (
      <button key={t.id} onClick={()=>onChange(t.id)} style={{
        padding:'8px 16px',borderRadius:8,border:'none',fontSize:14,
        fontWeight:active===t.id?600:500,fontFamily:"'Inter',sans-serif",
        background:active===t.id?'var(--accent)':'transparent',
        color:active===t.id?'#fff':'var(--muted)',
        boxShadow:active===t.id?'0 2px 8px rgba(22,163,74,0.2)':'none',
        cursor:'pointer',transition:'all 0.15s',whiteSpace:'nowrap',
      }}>{t.label}</button>
    ))}
  </div>
);

// ═══════════════════════════════════════════════════════════
// INPUT FIELD — REDE style: 46px, 10px radius, accent focus ring
// ═══════════════════════════════════════════════════════════
export const InputField = ({label,name,value,onChange,type='text',prefix,suffix,error,placeholder,tip}) => {
  const isNumeric = type === 'number';
  const handleChange = (e) => {
    if (isNumeric && prefix === '$') {
      const raw = e.target.value.replace(/[^0-9.]/g,'');
      onChange({target:{name,value:raw}});
    } else onChange(e);
  };
  const displayVal = isNumeric && prefix === '$' && value
    ? Number(value).toLocaleString('en-US')
    : value || '';
  return (
    <div style={{marginBottom:16}}>
      <div style={{display:'flex',alignItems:'center',gap:4,marginBottom:5}}>
        <label style={{fontSize:11,fontWeight:500,color:'var(--muted)'}}>{label}</label>
        {tip && <Tooltip_ text={tip}><span/></Tooltip_>}
      </div>
      <div style={{
        display:'flex',alignItems:'center',minHeight:46,
        background:'var(--bg-input)',border:`1px solid ${error?'var(--negative)':'var(--border)'}`,
        borderRadius:10,padding:'12px 14px',gap:4,transition:'border-color 0.2s, box-shadow 0.2s',
      }}>
        {prefix&&<span style={{color:'var(--muted)',fontSize:14,fontFamily:"'JetBrains Mono',monospace"}}>{prefix}</span>}
        <input name={name} value={displayVal} onChange={handleChange}
          type={isNumeric&&prefix==='$'?'text':type}
          inputMode={isNumeric?'decimal':undefined}
          placeholder={placeholder||''}
          style={{
            flex:1,border:'none',background:'transparent',outline:'none',
            color:'var(--text)',fontSize:15,fontFamily:"'JetBrains Mono',monospace",width:'100%',
          }}/>
        {suffix&&<span style={{color:'var(--muted)',fontSize:13,fontFamily:"'JetBrains Mono',monospace"}}>{suffix}</span>}
      </div>
      {error&&<div style={{color:'var(--negative)',fontSize:12,marginTop:4}}>{error}</div>}
    </div>
  );
};

export const SelectField = ({label,name,value,onChange,options,tip}) => (
  <div style={{marginBottom:16}}>
    <div style={{display:'flex',alignItems:'center',gap:4,marginBottom:5}}>
      <label style={{fontSize:11,fontWeight:500,color:'var(--muted)'}}>{label}</label>
      {tip && <Tooltip_ text={tip}><span/></Tooltip_>}
    </div>
    <select name={name} value={value} onChange={onChange} style={{
      width:'100%',minHeight:46,padding:'12px 14px',borderRadius:10,
      border:'1px solid var(--border)',background:'var(--bg-input)',
      color:'var(--text)',fontSize:15,fontFamily:"'JetBrains Mono',monospace",
      cursor:'pointer',outline:'none',
    }}>
      {options.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
);

export const Slider = ({label,min,max,step=1,value,displayValue,onChange,tip}) => (
  <div style={{marginBottom:14}}>
    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:6}}>
      <div style={{display:'flex',alignItems:'center',gap:4}}>
        <span style={{fontSize:11,fontWeight:500,color:'var(--muted)'}}>{label}</span>
        {tip && <Tooltip_ text={tip}><span/></Tooltip_>}
      </div>
      <span style={{fontSize:14,fontWeight:600,color:'var(--text)',fontFamily:"'JetBrains Mono',monospace"}}>{displayValue}</span>
    </div>
    <input type="range" min={min} max={max} step={step} value={value} onChange={onChange}
      style={{width:'100%',height:6,borderRadius:3,background:'var(--border)',outline:'none'}}/>
  </div>
);

// ═══════════════════════════════════════════════════════════
// THEME TOGGLE
// ═══════════════════════════════════════════════════════════
export const ThemeToggle = ({dark,setDark}) => (
  <button onClick={()=>setDark(!dark)} title={dark?'Light mode':'Dark mode'} style={{
    width:36,height:36,borderRadius:8,border:'1px solid var(--border)',
    background:'transparent',color:'var(--text)',fontSize:16,
    cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',
  }}>{dark?'☀':'☾'}</button>
);

// ═══════════════════════════════════════════════════════════
// VHG LOGO SVG (reusable)
// ═══════════════════════════════════════════════════════════
export const VHGLogo = ({dark,size=70}) => (
  <svg viewBox="0 0 320 170" width={size} height={size*0.53} style={{flexShrink:0}}>
    <path d="M85 28 L110 12 L128 22 L155 4 L178 18 L195 10 L235 28" fill="none" stroke="#10B981" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round"/>
    <line x1="80" y1="28" x2="240" y2="28" stroke="#C8962E" strokeWidth="0.8"/>
    <text x="160" y="62" textAnchor="middle" fill={dark?'#FFFFFF':'#0F172A'} fontFamily="Georgia,serif" fontSize="34" fontWeight="700" letterSpacing="0.08em">VACATION</text>
    <text x="160" y="95" textAnchor="middle" fill={dark?'#FFFFFF':'#0F172A'} fontFamily="Georgia,serif" fontSize="34" fontWeight="700" letterSpacing="0.08em">HOME</text>
    <line x1="60" y1="103" x2="112" y2="103" stroke="#C8962E" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="60" y1="109" x2="98" y2="109" stroke="#C8962E" strokeWidth="1" strokeLinecap="round"/>
    <text x="165" y="138" textAnchor="middle" fill="#C8962E" fontFamily="Georgia,serif" fontSize="32" fontStyle="italic">group</text>
  </svg>
);

// ═══════════════════════════════════════════════════════════
// NAV BAR — REDE style: panel bg, border-bottom, no shadow
// ═══════════════════════════════════════════════════════════
export const REDEMark = ({size=70}) => (
  <div style={{width:size,height:size*0.53,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
    <span style={{fontFamily:"'JetBrains Mono',monospace",fontWeight:800,fontSize:20,letterSpacing:'0.14em',color:'var(--text)'}}>REDE</span>
  </div>
);

export const NavBar = ({dark,setDark,onNav,brand}) => {
  const _bi = (brand && !brand.error) ? (brand.brand_identity || {}) : null;
  const branded = !!_bi;
  const agentName = branded ? (_bi.name || (brand.brokerage_identity && brand.brokerage_identity.legal_name) || (brand.agent_profile && brand.agent_profile.name) || 'Vacation Home Group') : 'Vacation Home Group';
  const logoUrl = branded ? (_bi.logo_url || '') : '';
  return (
  <nav style={{
    position:'sticky',top:0,zIndex:100,
    background:'var(--panel,var(--nav-bg))',
    borderBottom:'1px solid var(--border)',
    padding:'12px 20px',
    display:'flex',alignItems:'center',justifyContent:'space-between',
  }}>
    <div style={{display:'flex',alignItems:'center',gap:10,cursor:'pointer',minWidth:0}} onClick={()=>onNav&&onNav('landing')}>
      {branded ? (logoUrl ? <img src={logoUrl} alt="" style={{maxHeight:38,maxWidth:120,flexShrink:0}}/> : <REDEMark size={70}/>) : <VHGLogo dark={dark} size={70}/>}
      <div style={{minWidth:0}}>
        <div style={{fontSize:17,fontWeight:800,color:'var(--text)',letterSpacing:'-0.02em',lineHeight:1}}>
          Property<span style={{color:'var(--gold)'}}>Path</span>
        </div>
        <div style={{fontSize:12,color:'var(--muted)'}}>
          by {branded ? agentName : 'Vacation Home Group'} <span style={{fontFamily:"'JetBrains Mono',monospace",fontSize:10,color:'var(--faint)'}}>v{APP_VERSION}</span>
        </div>
      </div>
    </div>
    <div style={{display:'flex',alignItems:'center',gap:8,flexShrink:0}}>
      {onNav&&<>
        <button onClick={()=>onNav('features')} style={{background:'none',border:'none',color:'var(--muted)',fontSize:14,cursor:'pointer',padding:'6px 10px'}}>Features</button>
        <button onClick={()=>onNav('howItWorks')} style={{background:'none',border:'none',color:'var(--muted)',fontSize:14,cursor:'pointer',padding:'6px 10px'}}>How It Works</button>
        <button onClick={()=>onNav('pro')} style={{background:'none',border:'none',color:'var(--muted)',fontSize:14,cursor:'pointer',padding:'6px 10px'}}>Standard vs Pro</button>
        <button onClick={()=>onNav('resources')} style={{background:'none',border:'none',color:'var(--muted)',fontSize:14,cursor:'pointer',padding:'6px 10px'}}>Resources</button>
        <button onClick={()=>onNav('calculator')} style={{background:'var(--gold-bright)',border:'2px solid var(--gold-bright)',color:'#fff',fontSize:13,fontWeight:700,cursor:'pointer',padding:'8px 18px',borderRadius:6,whiteSpace:'nowrap'}}>Try It Free →</button>
      </>}
      <ThemeToggle dark={dark} setDark={setDark}/>
    </div>
  </nav>
  );
};

// ═══════════════════════════════════════════════════════════
// APP HEADER — STRcalc style: logo, name, Save/PDF/STD/PRO/theme
// ═══════════════════════════════════════════════════════════
export const AppHeader = ({dark, setDark, isPro, isUnlocked, onProClick, onTogglePro, onSave, onLoad, onPDF, brand: brandProp}) => {
  // Read brand from the shared runtime holder if it wasn't passed as a prop (app view doesn't thread it)
  const [brandFromState, setBrandFromState] = useState(getBrandPayload());
  useEffect(() => subscribeBrand(setBrandFromState), []);
  const brand = brandProp || brandFromState;
  // If brand payload from REDE, use agent branding
  const agentName = brand?.agent_profile?.name;
  const brandName = brand?.brand_identity?.name || agentName;
  const logoUrl = brand?.brand_identity?.logo_url;

  return (
  <div style={{marginBottom:16}}>
    <div style={{
      background:'var(--panel)',
      borderBottom:'1px solid var(--border)',
      borderRadius:isUnlocked?'12px 12px 0 0':'12px',
      boxShadow:'var(--card-shadow)',
      padding:'14px 20px',
      display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:10,
    }}>
      <div style={{display:'flex',alignItems:'center',gap:14}}>
        {logoUrl ? (
          <img src={logoUrl} alt="" style={{height:42,borderRadius:8}}/>
        ) : (
          <VHGLogo dark={dark} size={80}/>
        )}
        <div>
          <div style={{display:'flex',alignItems:'baseline',gap:8,flexWrap:'wrap'}}>
            <span style={{fontSize:22,fontWeight:800,color:'var(--text)',letterSpacing:'-0.02em'}}>
              Property<span style={{color:'var(--gold)'}}>Path</span>
            </span>
            <span style={{fontSize:13,color:'var(--muted)'}}>by {brandName || 'Vacation Home Group'}</span>
          </div>
          <div style={{fontSize:12,color:'var(--faint)',fontFamily:"'JetBrains Mono',monospace",marginTop:3}}>
            {brand?.brand_identity?.tagline || 'Short-Term Rental Investment Advisor'} · v{APP_VERSION}
          </div>
        </div>
      </div>
      <div style={{display:'flex',alignItems:'center',gap:6}}>
        {onSave&&<button onClick={onSave} title="Save Property" style={{width:38,height:38,borderRadius:8,border:'1px solid var(--border)',background:'transparent',color:'var(--gold)',fontSize:17,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}>💾</button>}
        {onLoad&&<button onClick={onLoad} title="Load Property" style={{width:38,height:38,borderRadius:8,border:'1px solid var(--border)',background:'transparent',color:'var(--accent)',fontSize:17,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}>📂</button>}
        {onPDF&&<button onClick={onPDF} title="PDF Report" style={{padding:'7px 14px',borderRadius:8,border:'1px solid var(--border)',background:'transparent',color:'var(--muted)',fontSize:13,cursor:'pointer',display:'flex',alignItems:'center',gap:4}}>
          <span style={{fontSize:14}}>📄</span> PDF
        </button>}
        <button onClick={()=>{if(isUnlocked&&onTogglePro)onTogglePro(false);}} style={{padding:'6px 14px',borderRadius:8,border:!isPro?'1.5px solid var(--muted)':'1px solid var(--border)',background:!isPro?'var(--bg)':'transparent',color:'var(--muted)',fontSize:13,fontWeight:!isPro?600:500,cursor:isUnlocked?'pointer':'default'}}>STD</button>
        <button onClick={()=>{if(isUnlocked){if(onTogglePro)onTogglePro(true);}else{if(onProClick)onProClick();}}} style={{padding:'6px 14px',borderRadius:8,border:isPro?'none':'1px solid var(--gold)',background:isPro?'var(--gold)':'transparent',color:isPro?'#fff':'var(--gold)',fontSize:13,fontWeight:600,cursor:'pointer',display:'flex',alignItems:'center',gap:4}}>
          {isPro&&<span style={{fontSize:12}}>★</span>} PRO
        </button>
        {setDark&&<ThemeToggle dark={dark} setDark={setDark}/>}
      </div>
    </div>
    {isUnlocked&&(
      <div style={{
        background:'var(--panel)',borderBottom:'1px solid var(--border)',
        borderLeft:'1px solid var(--border)',borderRight:'1px solid var(--border)',
        borderRadius:'0 0 12px 12px',padding:'5px 20px',
        display:'flex',justifyContent:'center',gap:8,
      }}>
        <span style={{fontSize:12,color:'var(--accent)'}}>✓ Pro</span>
        <span style={{fontSize:12,color:'var(--muted)'}}>Tax Benefits · Mortgage · What-If · AI Summary · Save & Compare · PDF · Email</span>
      </div>
    )}
  </div>
  );
};

// ═══════════════════════════════════════════════════════════
// VHG FOOTER LOGO
// ═══════════════════════════════════════════════════════════
export const VHGFooterLogo = ({dark}) => (
  <div style={{textAlign:'center',marginBottom:16}}>
    <VHGLogo dark={dark} size={180}/>
  </div>
);

// ═══════════════════════════════════════════════════════════
// PRO GATE — 2-step email verification
// ═══════════════════════════════════════════════════════════
export const ProGate = ({onUnlock,onClose,discoveryData,slug}) => {
  const [step,setStep]=useState('info');
  const [name,setName]=useState('');
  const [email,setEmail]=useState('');
  const [phone,setPhone]=useState('');
  const [code,setCode]=useState('');
  const [token,setToken]=useState('');
  const [loading,setLoading]=useState(false);
  const [error,setError]=useState('');

  const sendCode = async () => {
    if(!name||!email) return setError('Name and email required');
    setLoading(true); setError('');
    try {
      const resp = await fetch('/api/send-code', {
        method:'POST', headers:{'Content-Type':'application/json'},
        body:JSON.stringify({email,name,phone,slug}),
      });
      const data = await resp.json();
      if(resp.ok && data.success) { setToken(data.token); setStep('verify'); }
      else { setError(data.error||'Failed to send code.'); }
    } catch(err) { setError('Network error.'); }
    setLoading(false);
  };

  const verifyCode = async () => {
    if(!code) return;
    setLoading(true); setError('');
    try {
      const resp = await fetch('/api/verify-code', {
        method:'POST', headers:{'Content-Type':'application/json'},
        body:JSON.stringify({code,token,name,phone,discoveryData:discoveryData||null,notifyEmails:(()=>{try{return localStorage.getItem('vhg-notify-emails')||'';}catch(_e){return '';}})(),slug}),
      });
      const data = await resp.json();
      if(resp.ok && data.success) {
        setStep('done');
        setTimeout(()=>{onUnlock({name:data.name||name,email,phone});},1500);
      } else { setError(data.error||'Invalid code.'); }
    } catch(err) { setError('Network error.'); }
    setLoading(false);
  };

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.6)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:20}} onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} style={{background:'var(--card)',borderRadius:12,boxShadow:'var(--card-shadow)',padding:32,maxWidth:400,width:'100%'}}>
        {step==='done'?(
          <div style={{textAlign:'center'}}>
            <div style={{fontSize:40,marginBottom:12}}>✓</div>
            <div style={{fontSize:18,fontWeight:700,color:'var(--accent)'}}>Pro Unlocked!</div>
          </div>
        ):step==='verify'?(
          <>
            <div style={{fontSize:16,fontWeight:700,color:'var(--text)',marginBottom:16}}>Enter verification code</div>
            <p style={{fontSize:13,color:'var(--muted)',marginBottom:16}}>We sent a 6-digit code to {email}</p>
            <InputField label="Verification code" name="code" value={code} onChange={e=>setCode(e.target.value)} placeholder="123456"/>
            {error&&<div style={{color:'var(--negative)',fontSize:13,marginBottom:12}}>{error}</div>}
            <button onClick={verifyCode} disabled={loading} style={{width:'100%',padding:'12px',borderRadius:8,border:'none',background:'var(--accent)',color:'#fff',fontSize:15,fontWeight:600,cursor:'pointer'}}>{loading?'Verifying...':'Verify'}</button>
          </>
        ):(
          <>
            <div style={{fontSize:16,fontWeight:700,color:'var(--text)',marginBottom:4}}>Unlock Pro — Free for VHG Clients</div>
            <p style={{fontSize:13,color:'var(--muted)',marginBottom:16}}>Tax Benefits, Mortgage Comparison, What-If Snapshots, AI Summary, and more.</p>
            <InputField label="Your name" name="name" value={name} onChange={e=>setName(e.target.value)} placeholder="Jane Smith"/>
            <InputField label="Email" name="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="jane@email.com"/>
            <InputField label="Phone (optional)" name="phone" value={phone} onChange={e=>setPhone(e.target.value)} placeholder="603-555-1234"/>
            {error&&<div style={{color:'var(--negative)',fontSize:13,marginBottom:12}}>{error}</div>}
            <button onClick={sendCode} disabled={loading} style={{width:'100%',padding:'12px',borderRadius:8,border:'none',background:'var(--accent)',color:'#fff',fontSize:15,fontWeight:600,cursor:'pointer'}}>{loading?'Sending...':'Send Verification Code'}</button>
            <p style={{fontSize:11,color:'var(--faint)',textAlign:'center',marginTop:12,lineHeight:1.6}}>By submitting, you consent to the collection of your email address and preferences for the purpose of delivering personalized market analysis. We do not sell or share your information with third parties.</p>
          </>
        )}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// FOOTER — minimal in app, full on landing page
// ═══════════════════════════════════════════════════════════
export const VHGFooter = ({dark, brand, onNav}) => {
  const B = require('../utils/brand').BRAND;

  // REDE brand payload (slug routes) wins; BRAND constants are the house default
  const ap = brand?.agent_profile;
  const bi = brand?.brand_identity;
  const bk = brand?.brokerage_identity;

  // A BRANDED agent inherits NOTHING from the house constants below (B.*).
  // Those are OUR brand, OUR firm, OUR address, OUR website. A field the
  // payload did not supply renders as absent, or falls back only within the
  // agent's OWN identity -- never to ours. Otherwise an agent at another
  // brokerage hands their client our name and our address on their own
  // marketing material. The house (no agent payload at all) keeps the B.*
  // values, because there they are correct.
  const branded = !!ap;

  const agents = ap ? [{ name: ap.name, title: ap.license_number ? `Agent lic. #${ap.license_number}` : 'Real Estate Professional', phone: ap.phone || '', email: ap.email || '' }] : B.agents;
  // Their brand, then their firm, then their own name. Only an agent who has
  // supplied none of the three falls through to the neutral product name --
  // the same chain STR Optimizer and STRcalc already use.
  const brandName = bi?.name || bk?.legal_name || ap?.name || (branded ? 'Real Estate Decision Engine' : B.company);
  const tagline = bi?.tagline || (branded ? '' : B.motto);
  const brokerageName = bk?.legal_name || (branded ? '' : B.brokerage);
  const brokerageDisclaimer = bk?.disclosure || '';
  const brokerageLicense = bk?.license_number || '';
  const brokerageAddress = bk?.address || (branded ? '' : (B.brokerageAddress || '120 N. South Rd. Unit C PMB 198, North Conway, NH 03860'));
  const website = bi?.website_url || (branded ? '' : B.website);
  const websiteDisplay = website ? website.replace(/^https?:\/\/(www\.)?/,'').replace(/\/$/,'') : (branded ? '' : B.websiteDisplay);
  const colHead = {fontFamily:"'JetBrains Mono',monospace",fontSize:11,letterSpacing:'0.14em',color:'var(--faint)',fontWeight:700,marginBottom:8};

  return (
    <footer style={{borderTop:'1px solid var(--border)',padding:'40px 24px 28px',marginTop:40}}>
      {/* v4.0.3 — three-column family standard (brand | contact | brokerage) */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(240px,1fr))',gap:34,maxWidth:1000,margin:'0 auto',textAlign:'left'}}>
        <div>
              <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:10}}>
            {bi?.logo_url ? <img src={bi.logo_url} alt="" style={{maxHeight:52}}/> : <VHGLogo dark={dark} size={96}/>}
            <span style={{fontSize:19,fontWeight:800,color:'var(--text)'}}>Property<span style={{color:'var(--gold)'}}>Path</span></span>
          </div>
          <div style={{fontSize:14,color:'var(--muted)'}}>A tool by {brandName}</div>
          {tagline&&<div style={{fontSize:14,color:'var(--muted)',fontStyle:'italic',fontFamily:'Georgia,serif',marginTop:6}}>{tagline}</div>}
        </div>
        <div>
          <div style={colHead}>CONTACT</div>
          {agents.map((agent,i)=>(
            <div key={i} style={{marginBottom:10}}>
              <div style={{fontSize:15,fontWeight:700,color:'var(--text)'}}>{agent.name}</div>
              <div style={{fontSize:13,color:'var(--muted)'}}>{agent.title}</div>
              {agent.phone&&<div style={{fontSize:14}}><a href={`tel:${agent.phone.replace(/[^0-9]/g,'')}`} style={{color:'var(--muted)',textDecoration:'none'}}>{agent.phone}</a></div>}
              {agent.email&&<div style={{fontSize:14}}><a href={`mailto:${agent.email}`} style={{color:'var(--gold)',textDecoration:'none'}}>{agent.email}</a></div>}
            </div>
          ))}
          {website&&<div style={{fontSize:14}}><a href={website} style={{color:'var(--gold)',textDecoration:'none'}}>{websiteDisplay}</a></div>}
        </div>
        <div>
          <div style={colHead}>BROKERAGE</div>
          {brokerageName&&<div style={{fontSize:14,color:'var(--muted)',lineHeight:1.8}}>{brokerageName}</div>}
          {brokerageAddress&&(()=>{const _ci=brokerageAddress.indexOf(',');return _ci>0?(<React.Fragment><div style={{fontSize:14,color:'var(--muted)',lineHeight:1.8}}>{brokerageAddress.slice(0,_ci).trim()}</div><div style={{fontSize:14,color:'var(--muted)',lineHeight:1.8}}>{brokerageAddress.slice(_ci+1).trim()}</div></React.Fragment>):(<div style={{fontSize:14,color:'var(--muted)',lineHeight:1.8}}>{brokerageAddress}</div>);})()}
          {brokerageLicense&&<div style={{fontSize:14,color:'var(--muted)',lineHeight:1.8}}>Brokerage lic. #{brokerageLicense}</div>}
          {brokerageDisclaimer&&<div style={{fontSize:12.5,color:'var(--muted)',fontStyle:'italic',marginTop:8}}>{brokerageDisclaimer}</div>}
        </div>
      </div>
      <div style={{borderTop:'1px solid var(--border)',maxWidth:1000,margin:'26px auto 0',paddingTop:22,textAlign:'center'}}>
        <p style={{fontSize:13,color:'var(--muted)',lineHeight:1.8,maxWidth:760,margin:'0 auto 14px'}}>{B.disclaimer}</p>
        <p style={{fontSize:13,color:'var(--muted)',lineHeight:1.8,maxWidth:760,margin:'0 auto 14px'}}>{B.emailConsent}</p>
        <div style={{display:'flex',justifyContent:'space-between',flexWrap:'wrap',gap:8,fontSize:12,color:'var(--faint)'}}>
          <span>© 2026 {brandName} · All rights reserved.</span>
          <span><button onClick={()=>onNav&&onNav('privacy')} style={{background:'none',border:'none',color:'var(--gold)',cursor:'pointer',fontSize:12,padding:0}}>Privacy Policy</button> · <button onClick={()=>onNav&&onNav('terms')} style={{background:'none',border:'none',color:'var(--gold)',cursor:'pointer',fontSize:12,padding:0}}>Terms</button> · Powered by REDE · v{APP_VERSION}</span>
        </div>
      </div>
    </footer>
  );
};

// ═══════════════════════════════════════════════════════════
// CHART TOOLTIP
// ═══════════════════════════════════════════════════════════
export const ChartTooltip = ({active,payload,label}) => {
  if(!active||!payload) return null;
  return (
    <div style={{background:'var(--tooltip-bg)',border:'1px solid var(--tooltip-border)',borderRadius:10,padding:12,fontSize:13,boxShadow:'var(--card-shadow)'}}>
      <p style={{color:'var(--accent)',fontWeight:600,marginBottom:6}}>Year {label}</p>
      {payload.map((p,i)=><p key={i} style={{color:p.color,margin:'2px 0'}}>{p.name}: {typeof p.value==='number'?new Intl.NumberFormat('en-US',{style:'currency',currency:'USD',minimumFractionDigits:0,maximumFractionDigits:0}).format(p.value):p.value}</p>)}
    </div>
  );
};

// LEGAL PAGES — REDE Legal Standard v1 (shared across all five client apps)
// CANONICAL. Identical in STRcalc, PropertyPath, Sellercalc, STR Optimizer and
// RentOrOwn except the CONFIG values below. No CSS variables (R1): every color
// is a literal hex. Always light (R2). Byline never invents a name (R3).
// No placeholder names in the body — rede-platform.js rewrites them at runtime.
const LEGAL_APP  = 'PropertyPath';
const LEGAL_MARK = ['Property', 'Path'];
const LEGAL_GOLD = '#9A7820';
const LEGAL_AI   = 'AI summaries, tax-benefit analysis, mortgage comparison, and What-If snapshots';
const LEGAL_L2   = 'your saved properties, scenarios and Pro status';
const LEGAL_L5   = ', your saved properties and, for Pro, your unlock status';
const LEGAL_ENTITY = 'REDE';
const LEGAL_EMAIL  = 'support@redeapps.com';

function legalBrand(d) {
  d = (d && !d.error) ? d : {};
  var bi = d.brand_identity || {}, br = d.brokerage_identity || {}, ap = d.agent_profile || {};
  var hex = function (v) {
    return (typeof v === 'string' && /^#[0-9A-Fa-f]{6}$/.test(v.trim())) ? v.trim() : '';
  };
  var name = bi.name || br.legal_name || ap.name || '';
  return {
    name: name,
    gold: hex(bi.accent_color) || hex(bi.primary_color) || LEGAL_GOLD
  };
}

function legalCSS(g) {
  return ''
  + '#rede-legal{background:#F5F5F5;min-height:100vh;font-family:Inter,-apple-system,sans-serif;}'
  + '#rede-legal .rl-banner{background:linear-gradient(135deg,#0B1120,#151D2E);border-bottom:3px solid ' + g + ';padding:32px 40px 28px;}'
  + '#rede-legal .rl-mark{font-size:22px;font-weight:800;letter-spacing:-.5px;color:#F8FAFC;line-height:1.1;}'
  + '#rede-legal .rl-mark span{color:' + g + ';}'
  + '#rede-legal .rl-by{font-family:Georgia,serif;font-size:14px;font-weight:700;font-style:italic;color:' + g + ';letter-spacing:.5px;margin-top:4px;}'
  + '#rede-legal .rl-wrap{max-width:860px;margin:0 auto;padding:36px 40px 72px;}'
  + '#rede-legal .rl-btn{background:none;border:1px solid ' + g + '66;border-radius:100px;color:' + g + ';cursor:pointer;font-family:inherit;font-size:13px;font-weight:700;padding:7px 16px;}'
  + '#rede-legal .rl-btn:hover{background:' + g + '14;}'
  + '#rede-legal .rl-top{margin-bottom:28px;}'
  + '#rede-legal .rl-eye{font-family:"JetBrains Mono",monospace;font-size:11px;font-weight:700;letter-spacing:.14em;text-transform:uppercase;color:' + g + ';}'
  + '#rede-legal h1{font-family:"Playfair Display",Georgia,serif;font-size:42px;font-weight:700;color:#0A0A0A;line-height:1.05;margin:8px 0 10px;}'
  + '#rede-legal h1 span{color:' + g + ';}'
  + '#rede-legal .rl-sub{font-family:"JetBrains Mono",monospace;font-size:12px;color:#6B7280;line-height:1.7;margin-bottom:36px;}'
  + '#rede-legal h2{font-size:18px;font-weight:800;color:#0A0A0A;margin:36px 0 0;}'
  + '#rede-legal h2::after{content:"";display:block;height:2px;background:linear-gradient(to right,' + g + ',transparent);margin:8px 0 12px;}'
  + '#rede-legal p{font-size:13px;color:#334155;line-height:1.8;margin:0 0 12px;}'
  + '#rede-legal strong{color:#0A0A0A;}'
  + '#rede-legal a{color:' + g + ';text-decoration:underline;}'
  + '#rede-legal .rl-foot{margin-top:56px;padding-top:20px;border-top:1px solid #D8D8D8;font-family:"JetBrains Mono",monospace;font-size:11px;color:#94A3B8;text-align:center;}'
  + '@media(max-width:640px){#rede-legal .rl-banner{padding:24px 20px 20px;}#rede-legal .rl-wrap{padding:28px 20px 56px;}#rede-legal h1{font-size:32px;}}'
  + '@media print{#rede-legal .rl-btn{display:none;}}';
}

const LEGAL_PRIVACY_BODY = `
<h2>1. Who this policy covers</h2>
<p>This tool is provided by ${LEGAL_ENTITY}, a real estate technology platform. When you use a version of this tool branded for a specific real estate agent, that agent is a party you are choosing to share your contact information with; the REDE platform operates the tool on their behalf. Questions about your data: <a href="mailto:${LEGAL_EMAIL}">${LEGAL_EMAIL}</a>.</p>

<h2>2. What ${LEGAL_APP} collects, and what leaves your device</h2>
<p>The numbers you enter into the calculator are processed <strong>in your browser</strong> to produce your results, and ${LEGAL_L2} are stored only in your browser. They are not sent to us or stored on our servers.</p>
<p>${LEGAL_APP} also offers optional <strong>AI features</strong> (Pro): ${LEGAL_AI}. When you choose to run one, the inputs for that analysis — such as the property address and your figures — are <strong>sent to a third-party AI provider to generate the result</strong>. ${LEGAL_ENTITY} does <strong>not store or read</strong> those inputs and does not use them for advertising; the AI provider processes them under its own terms. If you don't use the AI features, nothing you type is transmitted.</p>
<p>If you unlock Pro or ask us to email your results, your email address is used to send your access code and any reports you request. Your name and email are also <strong>provided to the real estate agent</strong> whose version of the tool you used (${LEGAL_ENTITY} on the house version). That agent controls their copy of your contact details and may follow up with you, including with marketing; you can ask them to stop at any time. <strong>We do not sell your information.</strong></p>

<h2>3. Service providers</h2>
<p>Where data leaves your device, it is handled only by the providers needed to run the service: <strong>Vercel</strong> (hosting), <strong>Supabase</strong> (database), the <strong>AI provider</strong> that generates analyses, and our email provider for verification codes and requested reports. Payment for paid agent subscriptions is handled by <strong>Stripe</strong>, which stores card details — we never see them.</p>

<h2>4. What we do not do</h2>
<p>We do not sell your personal information. We do not use Google Analytics, the Meta Pixel, or any advertising or cross-site tracking technology on this tool. We do not build advertising profiles from your data.</p>

<h2>5. Cookies &amp; local storage</h2>
<p>This tool uses only what is strictly necessary to function — remembering your light/dark theme choice${LEGAL_L5} — stored in your browser's local storage, not in tracking cookies. No advertising or cross-site cookies are set.</p>

<h2>6. Your choices and rights</h2>
<p>You can use the calculator without giving us any personal information. If you have given us your email, you can ask us to delete our copy at any time via <a href="mailto:${LEGAL_EMAIL}">${LEGAL_EMAIL}</a> (to have an agent delete their copy, contact that agent). If you are a resident of California or another state with a comprehensive privacy law, you have the right to know what personal information we hold, to request its deletion, and to not be discriminated against for exercising those rights.</p>

<h2>7. Children</h2>
<p>This tool is intended for adults making real estate decisions and is not directed to children under 13. We do not knowingly collect information from children.</p>

<h2>8. Data security</h2>
<p>Where data is transmitted, it is sent over encrypted (HTTPS) connections. No method of transmission or storage is perfectly secure, and we cannot guarantee absolute security.</p>

<h2>9. Changes</h2>
<p>We may update this policy from time to time. The "last updated" date above reflects the current version.</p>

<h2>10. Contact</h2>
<p><a href="mailto:${LEGAL_EMAIL}">${LEGAL_EMAIL}</a> · ${LEGAL_ENTITY}.</p>
`;

const LEGAL_TERMS_BODY = `
<h2>1. Agreement</h2>
<p>By using this tool you agree to these Terms and to our Privacy Policy. If you do not agree, do not use the tool.</p>

<h2>2. What this tool is</h2>
<p>${LEGAL_APP} is a real estate decision-support tool provided by ${LEGAL_ENTITY} and, where branded for a specific agent, offered to you by that agent. It produces <strong>estimates</strong> to help you think through a decision. Where an analysis is generated by AI, that output may be imperfect and is not the service of, or endorsed by, the AI provider.</p>

<h2>3. Not professional advice — no guarantees</h2>
<p>Outputs are estimates based on the inputs you provide and simplified assumptions. This tool does not constitute financial, tax, legal, or investment advice, and does not guarantee any outcome, return, price, or result. Consult a qualified real estate professional, CPA, or financial advisor before making a decision. ${LEGAL_ENTITY}, its operator, and any agent or brokerage offering a branded version assume no liability for decisions made based on this tool, and use of this tool creates no agency, brokerage, fiduciary, or client relationship.</p>

<h2>4. Disclaimer of warranties</h2>
<p>The tool is provided "as is" and "as available," without warranties of any kind, express or implied, including fitness for a particular purpose. We do not warrant that it will be uninterrupted, error-free, or that any figure it produces is accurate or complete.</p>

<h2>5. Limitation of liability</h2>
<p>To the maximum extent permitted by law, the provider and its affiliates will not be liable for any indirect, incidental, special, or consequential damages, or any loss of profit or opportunity, arising from your use of the tool. Because the tool is provided free of charge to consumers, total liability for any claim is limited to one hundred US dollars ($100).</p>

<h2>6. Acceptable use</h2>
<p>You agree not to scrape, reverse engineer, copy, or build a competing product from this tool, its design, or its content, and not to remove any proprietary notices. Automated access is not permitted except as expressly authorized.</p>

<h2>7. Intellectual property</h2>
<p>The tool, its design, and its content are the property of ${LEGAL_ENTITY}. © 2026 ${LEGAL_ENTITY}. All rights reserved.</p>

<h2>8. Paid subscriptions (agents)</h2>
<p>Consumers use this tool free of charge. Real estate agents who subscribe to brand and deploy the tool are billed by Stripe on the terms shown at signup, renew automatically until cancelled, and may cancel at any time effective at the end of the current billing period.</p>

<h2>9. Changes and termination</h2>
<p>We may modify these Terms or discontinue the tool at any time. Continued use after a change means you accept the updated Terms.</p>

<h2>10. Governing law</h2>
<p>These Terms are governed by the laws of the State of New Hampshire, without regard to conflict-of-laws rules. Nothing here limits any consumer-protection rights that apply to you by law. For an agent-branded deployment, the agent's own obligations to you may also be subject to the laws of the agent's state.</p>

<h2>11. Contact</h2>
<p><a href="mailto:${LEGAL_EMAIL}">${LEGAL_EMAIL}</a> · ${LEGAL_ENTITY}.</p>
`;

function legalHTML(which, b) {
  var isT = (which === 'terms');
  var byline = b.name ? '<div class="rl-by">by ' + b.name + '</div>' : '';
  var forline = b.name
    ? 'Last updated: July 20, 2026 &nbsp;·&nbsp; for ' + LEGAL_APP + ', a tool by ' + b.name
    : 'Last updated: July 20, 2026 &nbsp;·&nbsp; for ' + LEGAL_APP;
  return ''
  + '<div class="rl-banner"><div class="rl-mark">' + LEGAL_MARK[0] + '<span>' + LEGAL_MARK[1] + '</span></div>' + byline + '</div>'
  + '<div class="rl-wrap">'
  +   '<div class="rl-top"><button type="button" class="rl-btn" data-legal="back">&larr; Back</button></div>'
  +   '<div class="rl-eye">' + (isT ? 'Terms' : 'Privacy') + '</div>'
  +   '<h1>' + (isT ? 'Terms of <span>Service</span>' : 'Privacy <span>Policy</span>') + '</h1>'
  +   '<div class="rl-sub">' + forline + '</div>'
  +   (isT ? LEGAL_TERMS_BODY : LEGAL_PRIVACY_BODY)
  +   '<div class="rl-foot"><button type="button" class="rl-btn" data-legal="save">&darr; Save a copy</button></div>'
  + '</div>';
}

function legalSave(which) {
  var node = document.getElementById('rede-legal');
  if (!node) return;
  var title = LEGAL_APP + ' — ' + (which === 'terms' ? 'Terms of Service' : 'Privacy Policy');
  var css   = legalCSS(legalBrand(window.__legalBrandData).gold);
  var html  = '<!DOCTYPE html><html lang="en"><head><meta charset="utf-8">'
    + '<meta name="viewport" content="width=device-width,initial-scale=1"><title>' + title + '</title>'
    + '<link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;700;800&family=JetBrains+Mono:wght@400;700&family=Playfair+Display:wght@700&display=swap" rel="stylesheet">'
    + '<style>body{margin:0}#rede-legal .rl-btn{display:none}' + css + '</style></head>'
    + '<body>' + node.outerHTML + '</body></html>';
  var url = URL.createObjectURL(new Blob([html], { type: 'text/html;charset=utf-8' }));
  var a = document.createElement('a');
  a.href = url;
  a.download = LEGAL_APP.replace(/\s+/g, '') + '-' + (which === 'terms' ? 'Terms' : 'Privacy') + '.html';
  a.click();
  setTimeout(function () { URL.revokeObjectURL(url); }, 2000);
}

function LegalPage({ which, brandData, onBack }) {
  const b = legalBrand(brandData);
  window.__legalBrandData = brandData;
  return (
    <>
      <style>{legalCSS(b.gold)}</style>
      <div
        id="rede-legal"
        onClick={(e) => {
          const t = e.target.closest('[data-legal]');
          if (!t) return;
          if (t.dataset.legal === 'back') { onBack && onBack(); }
          if (t.dataset.legal === 'save') { legalSave(which); }
        }}
        dangerouslySetInnerHTML={{ __html: legalHTML(which, b) }}
      />
    </>
  );
}
export function PrivacyPolicy({ brand, onBack })  { return <LegalPage which="privacy" brandData={brand} onBack={onBack} />; }
export function TermsOfService({ brand, onBack }) { return <LegalPage which="terms"   brandData={brand} onBack={onBack} />; }
